import type { FC } from "react"
import { useMemo } from "react"
import { Link } from "@tanstack/react-router"

import type { Company } from "@productlane/zero"
import type { UpdateCompanyArgs } from "@productlane/zero/types"
import { cn } from "@productlane/lib/src/cn"
import { formatRevenue } from "@productlane/lib/src/format-revenue"
import { CustomerRevenueIcon, CustomerSizeIcon } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useSession } from "@/lib/session"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { useDefaultCustomerStatus } from "./default-customer-status"
import { InputChange } from "./input-change"
import { OwnerSelect } from "./owner-select"
import { StageSelect } from "./stage-select"
import { TierSelect } from "./tier-select"

interface Props {
	company: Company
	variant?: "row" | "column"
}

const onValidate = (value: number | string) => {
	const newValue = Number(value)

	if (isNaN(newValue)) {
		return "Value must be a number"
	}

	if (newValue < 0) {
		return "Value must be equal or greater than 0"
	}

	if (newValue > 2147483647) {
		return "Annual revenue must be less than or equal to 2147483647"
	}

	return ""
}

export const CustomerProperties: FC<Props> = ({
	company,
	variant = "column",
}) => {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const defaultStatus = useDefaultCustomerStatus()

	const UserIcon = useMemo(() => CustomerSizeIcon, [])
	const BillIcon = useMemo(() => CustomerRevenueIcon, [])
	const revenueSuffix = workspace?.revenueFormatting === "ANNUAL" ? "yr" : "mo"

	type CustomerUpdateParams = {
		id?: string
	} & Omit<UpdateCompanyArgs, "id">
	const { session } = useSession()

	const handleCustomerUpdate = async (params: CustomerUpdateParams) => {
		if (!session) {
			return
		}

		const result = await zero.mutate(
			mutators.companies.update({ ...params, id: company.id }),
		).client

		if (result.type === "error") {
			toast.error({
				title: "Failed to update customer",
				description:
					result.error?.message ?? "Failed to update customer properties",
			})
			throw result.error
		}
	}

	return (
		<>
			{hasLinearConnection(workspace) && (
				<div
					className={cn("space-y-0.5", {
						"flex flex-row items-end gap-8": variant === "row",
					})}
				>
					<div className={cn("flex flex-col gap-1")}>
						<div
							className={cn("text-xs font-normal text-secondary", {
								hidden: variant === "column",
							})}
						>
							Status
						</div>
						<StageSelect
							modal={false}
							item={{
								id: company?.statusId ?? defaultStatus.id,
								name: company?.statusName ?? defaultStatus.name,
								color: company?.statusColor ?? defaultStatus.color,
							}}
							onChange={async ({ id, name, color }) => {
								await handleCustomerUpdate({
									id: company?.id,
									statusId: id,
									statusName: name,
									statusColor: color,
								})
							}}
							align={variant === "row" ? "start" : "start"}
							side={variant === "row" ? "right" : "left"}
							className={cn({
								"-mx-2": variant === "row",
							})}
						/>
					</div>

					<div className="flex flex-col gap-1">
						<div
							className={cn("text-xs font-normal text-secondary", {
								hidden: variant === "column",
							})}
						>
							Tier
						</div>
						<TierSelect
							modal={false}
							item={{
								id: company?.tierId ?? null,
								name: company?.tierName ?? null,
							}}
							onChange={async ({ id, name }) => {
								await handleCustomerUpdate({
									id: company?.id,
									tierId: id,
									tierName: name,
								})
							}}
							align={variant === "row" ? "start" : "start"}
							side={variant === "row" ? "right" : "left"}
							className={cn({
								"-mx-2": variant === "row",
							})}
						/>
					</div>

					<div className="flex flex-col gap-1">
						<div
							className={cn("text-xs font-normal text-secondary", {
								hidden: variant === "column",
							})}
						>
							Revenue
						</div>
						<InputChange
							name="Revenue"
							defaultValue={
								company.revenue == null
									? null
									: workspace.revenueFormatting === "ANNUAL"
										? company.revenue
										: Number(company.revenue) / 12
							}
							icon={BillIcon}
							placeholder="500"
							onChange={async (revenue) => {
								await handleCustomerUpdate({
									id: company?.id,
									revenue:
										workspace.revenueFormatting === "MONTHLY"
											? Number(revenue) * 12
											: Number(revenue),
								})
							}}
							postfix={variant === "column" ? `/${revenueSuffix}` : ""}
							renderValue={() => (
								<>
									<span>
										{formatRevenue({
											revenue: Number(company.revenue),
											revenueFormatting: workspace.revenueFormatting,
											currency: workspace.revenueCurrency,
										})}
									</span>
									<span className="text-tertiary">/{revenueSuffix}</span>
								</>
							)}
							onValidate={onValidate}
							className={cn({
								"-mx-2": variant === "row",
							})}
						/>
					</div>

					<div className="flex flex-col gap-1">
						<div
							className={cn("text-xs font-normal text-secondary", {
								hidden: variant === "column",
							})}
						>
							Size
						</div>
						<InputChange
							name="Size"
							defaultValue={company?.size ?? null}
							icon={UserIcon}
							placeholder="10"
							onChange={async (size) => {
								await handleCustomerUpdate({
									id: company?.id,
									size: Number(size),
								})
							}}
							onValidate={onValidate}
							className={cn({
								"-mx-2": variant === "row",
							})}
						/>
					</div>

					<div className="flex flex-col gap-1">
						<div
							className={cn("text-xs font-normal text-secondary", {
								hidden: variant === "column",
							})}
						>
							Owner
						</div>
						<OwnerSelect
							modal={false}
							item={{
								id: company?.linearOwnerId ?? null,
								name: company?.linearOwnerName ?? null,
								avatarUrl: company?.linearOwnerAvatarUrl ?? null,
							}}
							onChange={async ({ id, name, avatarUrl }) => {
								await handleCustomerUpdate({
									id: company?.id,
									linearOwnerId: id,
									linearOwnerName: name,
									linearOwnerAvatarUrl: avatarUrl,
								})
							}}
							className={cn({
								"-mx-2": variant === "row",
							})}
						/>
					</div>
				</div>
			)}
			{!hasLinearConnection(workspace) && (
				<div className="px-2 text-xs font-normal">
					<span className="text-xs font-normal text-tertiary">
						Connect Linear and enable Customer Requests to edit them from within
						Productlane.
					</span>{" "}
					<Link to="/settings/integrations/linear">Setup Linear</Link>
				</div>
			)}
		</>
	)
}
